/**
 * Presentation rules for the Accounting section.
 *
 * The backend sends money as integer cents and already computes every status,
 * total and phrase. This file exists so the pages agree on how those are
 * *shown* — one status palette, one money format, one place that knows a
 * negative amount wears parentheses.
 *
 * Nothing here recomputes a financial figure. If a number is wrong, it is
 * wrong on the server, which is where the arithmetic is tested.
 */

export const CURRENCY = 'CAD';

/** `-500000` -> `$(5,000.00)`. Mirrors `format_money` in accounting_allocation.py. */
export function formatCents(cents, currency = CURRENCY) {
  const amount = Number.isFinite(Number(cents)) ? Number(cents) : 0;
  const text = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
  }).format(Math.abs(amount) / 100);
  return amount < 0 ? `(${text})` : text;
}

/** A dollar string from a form -> cents, for optimistic display only. */
export function dollarsToCents(value) {
  const amount = Number.parseFloat(String(value ?? '').replace(/[$,\s]/g, ''));
  if (!Number.isFinite(amount)) return 0;
  return Math.round(Math.abs(amount) * 100) * Math.sign(amount || 1);
}

export function centsToDollarInput(cents) {
  const amount = Number.isFinite(Number(cents)) ? Number(cents) : 0;
  return (amount / 100).toFixed(2);
}

/**
 * Status colours, drawn from the admin palette in AdminTableStyles so a status
 * pill in Accounting reads the same as one in Agencies.
 */
export const INVOICE_STATUS_META = {
  draft: { label: 'Draft', tone: 'slate' },
  sent: { label: 'Confirmed (Issued)', tone: 'neutral' },
  partial: { label: 'Partial', tone: 'amber' },
  paid: { label: 'Paid', tone: 'emerald' },
  overdue: { label: 'Overdue', tone: 'red' },
  void: { label: 'Void', tone: 'slate' },
};

export const BILL_STATUS_META = {
  draft: { label: 'Draft', tone: 'slate' },
  open: { label: 'Open', tone: 'neutral' },
  partial: { label: 'Partial', tone: 'amber' },
  paid: { label: 'Paid', tone: 'emerald' },
  overdue: { label: 'Overdue', tone: 'red' },
  void: { label: 'Void', tone: 'slate' },
};

/**
 * Where a cheque has got to, which is a different question from whether the
 * bill is paid.
 *
 * A bill backed by an issued cheque reads "Paid" — the college has parted with
 * the money — while the cheque itself has not yet been presented at the bank.
 * Both facts are true and the page shows both. `none` is a payable with no
 * cheque behind it at all, which is not a defect and is captioned as such.
 */
export const CHEQUE_STATUS_META = {
  draft: { label: 'Draft', tone: 'slate', meaning: 'Written but not yet handed over.' },
  issued: { label: 'Issued', tone: 'amber', meaning: 'Handed to the payee. Not yet taken from the account.' },
  cleared: { label: 'Cleared', tone: 'emerald', meaning: 'Presented and paid by the bank.' },
  void: { label: 'Void', tone: 'red', meaning: 'Cancelled. This cheque moved no money.' },
  none: { label: 'No cheque', tone: 'neutral', meaning: 'No cheque has been written for this payment.' },
};

export function chequeStatusMeta(status) {
  return CHEQUE_STATUS_META[String(status || 'none').toLowerCase()] || CHEQUE_STATUS_META.none;
}

/**
 * An ISO timestamp as a short local date, or an empty string.
 *
 * Cheque transitions are stamped as full UTC timestamps; a table column wants
 * the day, not the microsecond.
 */
export function shortDate(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text.slice(0, 10);
  return parsed.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Money in, money out. Deposits read green, withdrawals read as a debit. */
export const TRANSACTION_DIRECTION_META = {
  deposit: { label: 'Deposit', tone: 'emerald', sign: '+' },
  withdrawal: { label: 'Withdrawal', tone: 'neutral', sign: '−' },
};

export function directionMeta(direction) {
  return TRANSACTION_DIRECTION_META[String(direction || '').toLowerCase()]
    || TRANSACTION_DIRECTION_META.withdrawal;
}

/**
 * Where a row came from: typed in here, or read from a workflow that owns it.
 *
 * This is the distinction that decides what a person is allowed to change, so
 * it is captioned on every row rather than left to be inferred from whether
 * the Edit button happens to be greyed out.
 */
export const ENTRY_MODE_META = {
  manual: { label: 'Manual', tone: 'neutral' },
  system: { label: 'From the portal', tone: 'gold' },
  imported: { label: 'Imported', tone: 'slate' },
};

export function entryModeMeta(mode) {
  return ENTRY_MODE_META[String(mode || '').toLowerCase()] || ENTRY_MODE_META.manual;
}

/**
 * Filter a grouped category list by what has been typed, keeping the groups.
 *
 * A category picker that flattens into one long list the moment you search
 * loses the only thing that made it readable, so groups that still have a
 * match survive and empty ones drop out.
 */
export function filterAccountGroups(groups, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return groups || [];
  return (groups || [])
    .map((group) => ({
      ...group,
      accounts: (group.accounts || []).filter(
        (account) =>
          account.name.toLowerCase().includes(needle)
          || (account.code || '').toLowerCase().includes(needle)
          || (account.description || '').toLowerCase().includes(needle)
      ),
    }))
    .filter((group) => group.accounts.length > 0);
}

/**
 * Do the split lines add up to the transaction?
 *
 * The server is the authority and refuses an unbalanced split outright. This
 * is the same arithmetic run while you type, so the difference is visible
 * before you press Save rather than after.
 */
export function splitBalance(amountCents, lines) {
  const allocated = (lines || []).reduce((sum, line) => sum + dollarsToCents(line?.amount), 0);
  const difference = Number(amountCents || 0) - allocated;
  return { allocatedCents: allocated, differenceCents: difference, balanced: difference === 0 };
}

export function invoiceStatusMeta(status) {
  return INVOICE_STATUS_META[String(status || '').toLowerCase()] || { label: status || 'Unknown', tone: 'neutral' };
}

export function billStatusMeta(status) {
  return BILL_STATUS_META[String(status || '').toLowerCase()] || { label: status || 'Unknown', tone: 'neutral' };
}

/**
 * How many filters the person has actually applied.
 *
 * Drives the "Filters (2)" count. A tab is not a filter — it is which list you
 * are looking at — so it is deliberately not counted here.
 */
export function activeFilterCount(filters, defaults) {
  return Object.keys(defaults).reduce((count, key) => {
    const current = filters?.[key];
    const fallback = defaults[key];
    const set = current !== undefined && current !== null && current !== '' && current !== fallback;
    return set ? count + 1 : count;
  }, 0);
}

/**
 * Read filter state out of the URL, so a filtered view can be shared, bookmarked
 * and survive a reload.
 */
export function filtersFromSearch(search, defaults) {
  const params = new URLSearchParams(search || '');
  return Object.keys(defaults).reduce((result, key) => {
    const value = params.get(key);
    return { ...result, [key]: value === null ? defaults[key] : value };
  }, {});
}

/** The inverse: only non-default values reach the URL, so it stays readable. */
export function searchFromFilters(filters, defaults) {
  const params = new URLSearchParams();
  Object.keys(defaults).forEach((key) => {
    const value = filters?.[key];
    if (value !== undefined && value !== null && value !== '' && value !== defaults[key]) {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** A readable message from an axios failure, never a raw stack. */
export function accountingError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first?.msg === 'string') return first.msg;
  }
  return fallback;
}

/**
 * The line-level arithmetic the create forms show while you type.
 *
 * This is a preview: the server recomputes every figure from its own catalog
 * and tax records before anything is stored, so a discrepancy here can never
 * become a wrong invoice.
 */
export function previewTotals(lines, taxesById) {
  let subtotalCents = 0;
  const taxTotals = new Map();

  (lines || []).forEach((line) => {
    const quantity = Number.parseFloat(line?.quantity ?? 1);
    const unitCents = dollarsToCents(line?.unit_price);
    const amount = Math.round((Number.isFinite(quantity) ? quantity : 1) * unitCents);
    subtotalCents += amount;
    (line?.tax_ids || []).forEach((taxId) => {
      const tax = taxesById?.[taxId];
      if (!tax) return;
      const charged = Math.round((amount * Number(tax.rate_basis_points || 0)) / 10000);
      taxTotals.set(taxId, (taxTotals.get(taxId) || 0) + charged);
    });
  });

  const taxes = [...taxTotals.entries()].map(([taxId, amountCents]) => ({
    tax_id: taxId,
    label: taxesById?.[taxId]?.label || '',
    amount_cents: amountCents,
  }));
  const taxTotalCents = taxes.reduce((sum, tax) => sum + tax.amount_cents, 0);

  return {
    subtotalCents,
    taxes,
    taxTotalCents,
    totalCents: subtotalCents + taxTotalCents,
  };
}


/**
 * Open a document response in a new tab.
 *
 * The invoice endpoint answers in one of two shapes: a JSON `{mode:'url'}`
 * pointing at a stored PDF in object storage, or the PDF bytes themselves for
 * an invoice rendered on demand. Both arrive as a Blob because the request asks
 * for one, so the JSON case is unwrapped here rather than at every call site.
 *
 * Returns the object URL it created, or null for the redirect case, so the
 * caller can revoke it once the tab has had a chance to load.
 */
/** Pop-up blocked: download instead, so the click always does something. */
function downloadViaAnchor(href, fallbackName) {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export async function openDocumentResponse(response, fallbackName = 'document.pdf') {
  const blob = response?.data;
  if (!blob) throw new Error('empty document response');

  if (blob.type && blob.type.includes('application/json')) {
    const parsed = JSON.parse(await blob.text());
    if (!parsed?.url) throw new Error('document response carried no url');
    // The same fallback the PDF branch below has, and for a sharper reason:
    // reading the JSON needs an await, which puts this window.open outside
    // the click that caused it, so browsers block it. Every application and
    // student invoice comes back through here, so without a fallback the
    // button did nothing at all — no document, and no error either.
    if (!window.open(parsed.url, '_blank', 'noopener,noreferrer')) {
      downloadViaAnchor(parsed.url, fallbackName);
    }
    return null;
  }

  const url = URL.createObjectURL(blob);
  if (!window.open(url, '_blank', 'noopener,noreferrer')) {
    downloadViaAnchor(url, fallbackName);
  }
  return url;
}
